/**
 * useFreigabe — TanStack Query hooks for the Freigabe/access workflow (PART C5).
 *
 * W1 (request):        useIssueAccessRequest — POST …/access-requests + pending state
 *                      via useRequestsForComm (a non-participant sees ONLY own requests,
 *                      so any pending row = "I have an open request").
 * W2 (decider inbox):  useDeciderRequests + useDecideRequest.
 * W3 (managed access): useAccessGrants + useGrantManagedAccess + useRevokeGrant.
 *
 * All queries are gated on live mode — in mock mode they stay disabled and return
 * empty data so the Kommunikation screen renders standalone (VITE_DATA_MODE=mock).
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useEingangMails.ts] (hook + mock-gate pattern)
 * [Source: apps/api/src/modules/communication/comm-access.service.ts] (endpoint contracts)
 * [Source: docs/design/2026-08-07_kommunikation-emailclient-tiers-freigabe.md#part-c5]
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import type {
  AccessRequestItem,
  ManagedAccessGrantItem,
  GrantManagedAccessBody,
  ListAccessGrantsParams,
  DecideAccessRequestBody,
} from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { decideFreigabeMock } from '../../posteingang/mock';

// ── W1: request Vollzugriff + pending state ──────────────────────────────────

/**
 * Access requests on one communication: a reduced-tier viewer gets only their own, a
 * participant gets all of them with names (Freigabe-Cockpit). `mock` feeds mock mode.
 */
export function useRequestsForComm(
  commId: string | null,
  enabled = true,
  mock?: (commId: string) => AccessRequestItem[],
) {
  const live = isLiveData();
  return useQuery<AccessRequestItem[]>({
    queryKey: qk.accessRequestsForComm(commId ?? 'none'),
    queryFn: () =>
      live
        ? apiClient.communications.listRequestsForComm(commId as string)
        : Promise.resolve(mock ? mock(commId as string) : []),
    enabled: (live || mock !== undefined) && enabled && commId !== null,
  });
}

/** True for the participant view (names present): only then is the cockpit shown. */
export function participantView(requests: AccessRequestItem[] | undefined): boolean {
  return (requests ?? []).some((r) => Boolean(r.requester_name?.trim()));
}

/**
 * POST /communications/:commId/access-requests — "Freigabe anfragen" (W1).
 *
 * Berechtigungskonzept V4.0 (Bereich 1, 3.3): the Begründung is a Pflichtfeld — the reason
 * is REQUIRED (the caller/UI must supply it; the server also validates min length → 400).
 */
export function useIssueAccessRequest(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commId, reason }: { commId: string; reason: string }) =>
      apiClient.communications.issueAccessRequest(commId, { reason }),
    onSuccess: (_res, { commId }) => {
      void qc.invalidateQueries({ queryKey: qk.accessRequestsForComm(commId) });
    },
    onError: () => onErrorToast?.('Freigabe-Anfrage fehlgeschlagen.'),
  });
}

// ── W2: decider inbox + decide ───────────────────────────────────────────────

/** GET /communications/access-requests?role=decider — the decider inbox (W2). */
export function useDeciderRequests(status: 'pending' | 'approved' | 'rejected' = 'pending') {
  const live = isLiveData();
  return useQuery<AccessRequestItem[]>({
    queryKey: qk.accessRequestsDecider({ status, role: 'decider' }),
    queryFn: () => apiClient.communications.listAccessRequests({ status, role: 'decider' }),
    enabled: live,
  });
}

/**
 * POST /communications/access-requests/:requestId/decision — grant or deny (W2).
 *
 * Berechtigungskonzept V3.0 (Bereich 1, 3.3): a GRANT may carry a duration (permanent /
 * 1 / 7 / 30 days / until a date → comm_access_grant.expires_at); a DECLINE requires a
 * ≥20-char review_note (audit-relevant, enforced server-side → a 400 the caller surfaces).
 */
export function useDecideRequest(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      ...body
    }: { requestId: string } & DecideAccessRequestBody) => {
      // Mock mode: the cockpit's demo requests are decided in memory.
      if (!isLiveData()) {
        decideFreigabeMock(requestId, body.decision);
        return undefined as unknown as Awaited<ReturnType<typeof apiClient.communications.decideAccessRequest>>;
      }
      return apiClient.communications.decideAccessRequest(requestId, body);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['communications', 'access-requests', 'decider'] });
      // The per-Eingang lists too (Freigabe-Cockpit): key ['communications', commId, 'access-requests', …].
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'communications' && q.queryKey[2] === 'access-requests',
      });
    },
    onError: () => onErrorToast?.('Entscheidung fehlgeschlagen.'),
  });
}

// ── W3: managed-access grant + list + revoke ─────────────────────────────────

/** GET /communications/access-grants — standing grants for a subject + own (W3). */
export function useAccessGrants(params: ListAccessGrantsParams, enabled = true) {
  const live = isLiveData();
  return useQuery<ManagedAccessGrantItem[]>({
    queryKey: qk.accessGrants(params as Record<string, unknown>),
    queryFn: () => apiClient.communications.listAccessGrants(params),
    enabled: live && enabled,
  });
}

/** POST /communications/access-grants — "Zugriff freigeben" (W3). */
export function useGrantManagedAccess(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantManagedAccessBody) =>
      apiClient.communications.grantManagedAccess(body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['communications', 'access-grants'] });
    },
    onError: () => onErrorToast?.('Freigabe konnte nicht erteilt werden.'),
  });
}

/** DELETE /communications/access-grants/:grantId — revoke (W3). */
export function useRevokeGrant(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => apiClient.communications.revokeAccessGrant(grantId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['communications', 'access-grants'] });
    },
    onError: () => onErrorToast?.('Freigabe konnte nicht widerrufen werden.'),
  });
}
